import { Box, TableCell } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { memo } from 'react';
import { cast } from '~/utils/helper';
import dayjs from 'dayjs';
import BaseTable from '~/components/common/base-table/BaseTable';
import { HeadCelType, SortDirection } from '~/utils/types/common';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { PAGE_INDEX, PAGE_SIZE } from '~/utils/constants/common';
import { actionLogActions } from '~/thunks/action-log/actionLogSlice';
import { IActionLog } from '~/utils/interface/actionLog';
import { TypeEnum } from '~/utils/enum';

const ActionLogList = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('admin/action-log');

  const { actionLogs, totalItems, filter } = useAppSelector((state) => state.actionLog);

  const headCells: Array<HeadCelType> = [
    {
      id: 'id',
      label: t('id'),
      sortable: false,
      searchable: false,
      className: 'w-12 border-l border-[#EBF1F8] border-b-0 text-center',
    },
    {
      id: 'type',
      label: t('type.title'),
      sortable: false,
      searchable: false,
      className: 'w-36 border-l border-[#EBF1F8] border-b-0 text-center',
    },
    {
      id: 'username',
      label: t('username'),
      sortable: true,
      searchable: true,
      className: 'w-60 border-l border-white border-b-0 text-center',
    },
    {
      id: 'description',
      label: t('description'),
      sortable: true,
      searchable: true,
      className: 'w-96 border-l border-white border-b-0 text-center',
    },
    {
      id: 'action_time',
      label: t('action_time'),
      sortable: false,
      searchable: false,
      className: 'w-36 border-l border-white border-b-0 text-center',
    },
  ];

  const handleChangePage = (newPage: number) => {
    dispatch(
      actionLogActions.setFilter({
        pageIndex: newPage,
      })
    );
  };

  const handleChangePageSize = (pageSize: number) => {
    dispatch(
      actionLogActions.setFilter({
        pageSize: pageSize,
        pageIndex: PAGE_INDEX,
      })
    );
  };

  const handleRequestSort = (sortBy: string, sortOrder: SortDirection) => {
    dispatch(
      actionLogActions.setFilter({
        sortBy: sortBy,
        sortOrder: sortOrder,
        pageIndex: PAGE_INDEX,
      })
    );
  };

  const handleRequestSearch = (searchBy: string, value: string) => {
    dispatch(
      actionLogActions.setFilter({
        search: value,
        searchBy: searchBy,
        pageIndex: PAGE_INDEX,
      })
    );
  };

  const getType = (type: number) => {
    if (type === TypeEnum.TAI_KHOAN) return t('type.' + TypeEnum.TAI_KHOAN);
    if (type === TypeEnum.HO_SO) return t('type.' + TypeEnum.HO_SO);
    if (type === TypeEnum.THIET_BI) return t('type.' + TypeEnum.THIET_BI);
    if (type === TypeEnum.HANG_RAO) return t('type.' + TypeEnum.HANG_RAO);
    if (type === TypeEnum.CANH_BAO) return t('type.' + TypeEnum.CANH_BAO);
  };

  return (
    <Box className='w-full'>
      <BaseTable<any>
        label={t('label')}
        data={actionLogs}
        pageIndex={filter.pageIndex || PAGE_INDEX}
        pageSize={filter.pageSize || PAGE_SIZE}
        totalItems={totalItems}
        headCells={headCells}
        sortBy={filter.sortBy}
        sortOrder={filter.sortOrder}
        searchInput={filter.search}
        onChangePage={handleChangePage}
        onChangePageSize={handleChangePageSize}
        onRequestSort={handleRequestSort}
        onRequestSearch={handleRequestSearch}
        renderCell={(row: IActionLog, index: number) => {
          return (
            <>
              <TableCell className='text-base font-normal text-[#161A1F]' align='center'>
                {index + PAGE_INDEX + (Number(filter.pageIndex || 0) - PAGE_INDEX) * PAGE_SIZE}
              </TableCell>
              <TableCell className='text-base font-normal text-[#161A1F]' align='center'>
                {getType(cast(row.type))}
              </TableCell>
              <TableCell className='text-base font-normal text-[#161A1F]' align='center'>
                {cast(row.username)}
              </TableCell>
              <TableCell className='text-base font-normal text-[#161A1F]'>{cast(row.description)}</TableCell>
              <TableCell className='text-base font-normal text-[#161A1F]' align='center'>
                {row.action_time ? dayjs(row.action_time).format('DD/MM/YYYY') : ''}
              </TableCell>
            </>
          );
        }}
      />
    </Box>
  );
};

export default memo(ActionLogList);
